import React, { useEffect, useState } from "react";
import { GridTable } from "@ctsu/ctsu-mui-grid";
import { useController, useFormState } from "react-hook-form";
import { OpenNotifyScreenReader } from "protocol-setup/components/notification";
import { openConfirmAlert } from "@ctsu/react-ctsu-ui";
import { FasCommentAltDots, FasPencilAlt, FasTrashAlt } from "assets/icons";
import text from "protocol-setup/constants/text";
import { titleAndAria } from "protocol-setup/constants/errorMessages";
import { useCurrentUser } from "protocol-setup/helpers/useIsCTSUUser";
import Select from "react-select";
import PSRModal from "./PSRModal";
import ViewPSR from "./ViewPSR";

const renderSubtitle = () => text.regulatory.psrTable.subtitle;

export const PSRActionsCell = ({ handleRowUpdate, handleRowDelete, rowData, value, control, approvalLOV, readOnly = false }) => {
  return (approvalLOV && approvalLOV.length > 0) ? (
    <>
      <Select
        name="psrs"
        options={approvalLOV}
        value={approvalLOV.find(opt => opt.value === rowData.action) || null}
        className="reactSelect-wrapper"
        classNamePrefix="reactSelect"
        styles={{ control: (provided) => ({ ...provided, minWidth: '8rem' }) }}
        menuPortalTarget={document.body}
        onChange={(selectedOption) => {
          const updatedRow = { ...rowData, action: selectedOption.value };
          handleRowUpdate(updatedRow);
        }}
      />
    </>
  ) : (
    <>
      <ViewPSR rowData={rowData} value={value} action="view">
        <FasCommentAltDots />
      </ViewPSR>
      <ViewPSR rowData={rowData} value={value} action="edit" handleRowUpdate={(d) => handleRowUpdate(d)}>
        <FasPencilAlt />
      </ViewPSR>
      <button
        data-id="psr-row-delete-row"
        type="button"
        className="grid-fn-btn delete"
        title={titleAndAria.removePsr}
        onClick={handleRowDelete}
      >
        <FasTrashAlt />
      </button>
    </>
  )
};

export default function PSRTable({ control, name = "psrDetails", psrList = [], trigger, readOnly = false, customCols = [], showAddPSR = true, approvalLOV = [] }) {
  const {
    field: { value = [], onChange }
  } = useController({
    name,
    control,
    defaultValue: []
  });
  const currentUser = useCurrentUser();
  const [PSRData, setPSRData] = useState(value);

  const { errors } = useFormState({ control });

  useEffect(() => {
    if (value.length > 0) {
      const updated = value.map(item => ({
        ...item,
        psrName1: item.psrName.toUpperCase()
      }));
      setPSRData(updated?.sort((a, b) => a.psrName1.localeCompare(b.psrName1)));
    } else {
      setPSRData([]);
    }
  }, [value])

  const handleOnChange = (updatedData, actionType) => {
    onChange(updatedData);
    trigger(name);
    OpenNotifyScreenReader({ message: `PSR ${actionType}`, align: "left", location: "bottom" });
  };

  const handleRowUpdate = (updatedRow) => {
    const trackbyId = updatedRow.trackbyId || updatedRow.psRpdId;
    const updatedData = value.map((row) => {
      const rowId = row.trackbyId || row.psRpdId;
      return rowId === trackbyId ? updatedRow : row;
    });
    handleOnChange(updatedData, "updated");
  };

  const handleRowDelete = (row) => {
    const psrName = row.psrName || "";
    const psrType = row.psrType || "";

    const { deleteModal } = text.regulatory.psrTable;

    openConfirmAlert({
      title: (
        <div className="d-flex align-items-center">
          <FasTrashAlt className="mr-2 color-red" />
          <span>{deleteModal.title}</span>
        </div>
      ),
      message: (
        <div>
          <p>
            <>{deleteModal.message1}</>
            <strong className="mx-1">
              {psrName} | {psrType}
            </strong>
            <>{deleteModal.message2}</>
          </p>
          <p>{deleteModal.message3}</p>
        </div>
      ),
      buttons: [
        {
          label: deleteModal.confirmBtn,
          onClick: () => {
            const trackbyId = row.trackbyId || row.psRpdId;
            const updatedData = value.filter((v) => {
              const rowId = v.trackbyId || v.psRpdId;
              return rowId !== trackbyId;
            });
            handleOnChange(updatedData, "deleted");
          },
          className: ""
        },
        {
          label: deleteModal.cancelBtn,
          onClick: () => { },
          className: "close"
        }
      ],
      closeOnEscape: true,
      closeOnClickOutside: true
    });
  };

  const handleAddPSR = (newPSRData) => {
    const trackbyId = Date.now().toString(36) + Math.random().toString(36).substring(2);
    const row = { ...newPSRData, trackbyId };
    const updatedData = [row, ...value];
    handleOnChange(updatedData, "added");
  };

  const columns = [
    {
      filterable: true,
      label: text.regulatory.psrTable.columns.psrName.label,
      name: "psrName1",
      type: "text",
      renderCell: (data) => data.psrName
    },
    {
      filterable: true,
      label: text.regulatory.psrTable.columns.psrType.label,
      name: "psrType",
      type: "text"
    },
    {
      filterable: true,
      label: text.regulatory.psrTable.columns.psrNumOfPersons.label,
      name: "psrNumOfPersons",
      type: "number"
    },
    {
      filterable: true,
      label: text.regulatory.psrTable.columns.isEvaluationByGroup.label,
      name: "isEvaluationByGroup",
      type: "object",
      renderCell: (data) => (data.isEvaluationByGroup ? "Yes" : "No"),
      options: [{ label: "Yes", value: true }, { label: "No", value: false }]
    },
    {
      filterable: true,
      label: text.regulatory.psrTable.columns.sourceSubmittedBy.label,
      name: "sourceSubmittedBy",
      type: "text"
    },
    {
      filterable: true,
      label: text.regulatory.psrTable.columns.ctsuProcessingInstructions.label,
      name: "ctsuProcessingInstructions",
      type: "text"
    },
    {
      filterable: true,
      label: text.regulatory.psrTable.columns.requestedStatus.label,
      name: "requestedStatus",
      type: "text"
    },
    {
      filterable: true,
      label: text.regulatory.psrTable.columns.modifiedDate.label,
      name: "modifiedDt",
      type: "text",
      visible: false
    },
    {
      filterable: false,
      sort: false,
      label: text.regulatory.psrTable.columns.actions.label,
      name: "",
      type: "text",
      renderCell: (rowData) =>
        !readOnly ? (
          <PSRActionsCell
            rowData={rowData}
            value={value}
            handleRowUpdate={(d) => handleRowUpdate(d)}
            handleRowDelete={() => handleRowDelete(rowData)}
            approvalLOV={approvalLOV}
            control={control}
            readOnly={readOnly}
          />
        ) : (
          <>
            {approvalLOV.length > 0 && approvalLOV.find((lov) => lov.value === rowData.action)?.label || rowData.action}
          </>
        )
    }
  ];

  return (
    <div className="psr-table grid-header-center-hidden mb-n4">
      <GridTable
        tag="psr"
        columns={customCols.length > 0 ? columns.filter(col => customCols.includes(col.name)) : columns}
        title={text.regulatory.psrTable.tableTitle}
        renderSubtitle={renderSubtitle}
        toExcel={false}
        toPdf={false}
        renderCustomHeaderIcons={() => (showAddPSR && !readOnly ? <PSRModal psrList={psrList} onAddPSR={handleAddPSR} /> : <></>)}
        recordPerPage={10}
        rows={PSRData}
        editMode={!readOnly}
        filterable
        sortable
        enableToolbar
        defaultSorts={[{ field: "psrName1", dir: "asc" }]}
        user={currentUser}
      />
      {errors[name] && <div className="form-layout-message-error mt-n4 mb-4">{errors[name].message}</div>}
    </div>
  );
}
